/**
 * The coverage-assessment introduction shown before account details, shared by guided planning and
 * the D2C self-funding sequence.
 *
 * Copy is the guided wording. Guided planning numbers its stages, so it passes `stepLabel="Step 1"`
 * ("Step 1: Coverage Assessment", "Let's Start Step 1"); the D2C sequence has no numbered stages and
 * omits it. Back appears only when the host passes `onBack`.
 */
import { Circle, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, cn } from '@design-system';

import type { FundingScreenFrame } from '../types';

export interface AssessmentIntroductionProps {
  stepLabel?: string;
  /** Fill the host's width (the consumer flow) instead of a centred column (guided planning). */
  fullWidth?: boolean;
  onContinue: () => void;
  onBack?: () => void;
  frame: FundingScreenFrame;
}

const ASSESSMENT_BULLETS = [
  'Get details on each of your accounts',
  'Project your future self-funding capacity',
  'Identify any coverage gaps',
];

export function AssessmentIntroduction({
  stepLabel,
  fullWidth = false,
  onContinue,
  onBack,
  frame,
}: AssessmentIntroductionProps) {
  const content = (
    <div className="flex flex-col items-center">
      <Card className={cn('w-full gap-3 bg-gray-50', !fullWidth && 'max-w-3xl')}>
        <CardHeader>
          <CardTitle>What we'll do:</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {ASSESSMENT_BULLETS.map(bullet => (
              <li
                key={bullet}
                className="flex items-start gap-3"
              >
                <Circle className="text-primary mt-1.5 size-3 shrink-0" />
                <span className="text-gray-700">{bullet}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );

  return frame({
    title: stepLabel ? `${stepLabel}: Coverage Assessment` : 'Coverage Assessment',
    subtitle:
      "First, we're going to figure out how much you have to cover long-term care today, whether it's through insurance you already own, self-funding, or other sources.",
    icon: (
      <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
        <TrendingUp className="text-primary size-7" />
      </div>
    ),
    content,
    primaryAction: {
      label: stepLabel ? `Let's Start ${stepLabel}` : "Let's start",
      onSelect: onContinue,
    },
    backAction: onBack ? { label: 'Back', onSelect: onBack } : undefined,
  });
}
